/**
 * SIATA Santa Elena georeferenced map overlay config.
 *
 * Georeference priority:
 * 1. GeoTIFF-derived metadata (server georef / georefFrame)
 * 2. Env override (REACT_APP_SIATA_SANTA_ELENA_RADAR_BOUNDS)
 * 3. Admin-calibrated bounds (server georef)
 * 4. Approved manual bounds
 * 5. Development placeholder (non-production only)
 *
 * Debug candidate bounds (admin calibration preview) never affect production rendering.
 */

import {
  SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS,
  SIATA_SANTA_ELENA_GEOREF_SOURCES,
} from "../../../constants/siataSantaElenaCalibrationConstants.js";
import {
  boundsToMapboxCoordinates,
  parseBoundsEnv,
  validateRadarBounds,
} from "../../../utils/siataSantaElenaRadarBoundsUtils.js";
import { boundsToGeoJson } from "../../../utils/ideamBartBoundsUtils.js";

export const SIATA_SANTA_ELENA_RADAR_SOURCE_ID = "weather-radar-siata-santa-elena-source";
export const SIATA_SANTA_ELENA_RADAR_LAYER_ID = "weather-radar-siata-santa-elena-layer";
export const SIATA_SANTA_ELENA_RADAR_BOUNDS_SOURCE_ID =
  "weather-radar-siata-santa-elena-bounds-source";
export const SIATA_SANTA_ELENA_RADAR_BOUNDS_LAYER_ID =
  "weather-radar-siata-santa-elena-bounds-layer";
export const SIATA_SANTA_ELENA_RADAR_REF_SOURCE_ID =
  "weather-radar-siata-santa-elena-ref-source";
export const SIATA_SANTA_ELENA_RADAR_REF_LAYER_ID =
  "weather-radar-siata-santa-elena-ref-layer";

export const SIATA_SANTA_ELENA_RADAR_DEV_PLACEHOLDER_BOUNDS = {
  ...SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS,
};

export const SIATA_SANTA_ELENA_RADAR_GEOREF = {
  sourceCode: "SIATA_SANTA_ELENA",
  sourceKey: "siata_santa_elena_radar",
  sourceLabel: "SIATA Santa Elena Radar",
  trustLabel: "Local official",
  typeLabel: "Local official radar",
  coverageLabel: "Aburrá Valley local coverage",
  attribution: "SIATA / Área Metropolitana del Valle de Aburrá",
  bounds: { ...SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS },
  boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.APPROVED_MANUAL,
  boundsConfirmed: false,
  geotiffPreferred: true,
  geotiffProduct: "AreaMetRadar_10_120_DBZH.tiff",
  mapboxCoordinates: boundsToMapboxCoordinates(SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS),
};

export const SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG = {
  sourceCode: SIATA_SANTA_ELENA_RADAR_GEOREF.sourceCode,
  sourceKey: SIATA_SANTA_ELENA_RADAR_GEOREF.sourceKey,
  sourceLabel: SIATA_SANTA_ELENA_RADAR_GEOREF.sourceLabel,
  trustLabel: SIATA_SANTA_ELENA_RADAR_GEOREF.trustLabel,
  typeLabel: SIATA_SANTA_ELENA_RADAR_GEOREF.typeLabel,
  coverageLabel: SIATA_SANTA_ELENA_RADAR_GEOREF.coverageLabel,
  attribution: SIATA_SANTA_ELENA_RADAR_GEOREF.attribution,
  developmentPreviewLabel:
    "SIATA Santa Elena Radar — Local official — development preview",
  updateIntervalMinutes: 5,
  defaultOpacity: 0.65,
  bounds: SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS,
};

function isProductionEnv() {
  return String(process.env.NODE_ENV || "").toLowerCase() === "production";
}

function resolveGeotiffBounds(georefFrame, serverGeoref) {
  const fromServer =
    serverGeoref?.activeGeorefSource === SIATA_SANTA_ELENA_GEOREF_SOURCES.GEOTIFF &&
    serverGeoref?.boundsValid &&
    serverGeoref?.bounds
      ? serverGeoref.bounds
      : null;

  if (fromServer && validateRadarBounds(fromServer).valid) {
    return fromServer;
  }

  const fromFrame =
    georefFrame?.geotiffMetadata?.ok && georefFrame?.geotiffMetadata?.bounds
      ? georefFrame.geotiffMetadata.bounds
      : georefFrame?.bounds;

  if (fromFrame && validateRadarBounds(fromFrame).valid) {
    return fromFrame;
  }

  return null;
}

/**
 * Resolve active SIATA radar bounds for overlay rendering.
 */
export function resolveSiataSantaElenaRadarBounds(
  georefFrameOrOptions = null,
  legacyServerGeoref = null
) {
  const options =
    georefFrameOrOptions && typeof georefFrameOrOptions === "object" && "georefFrame" in georefFrameOrOptions
      ? georefFrameOrOptions
      : {
          georefFrame: georefFrameOrOptions,
          serverGeoref: legacyServerGeoref,
        };

  const {
    georefFrame = null,
    serverGeoref = null,
    candidateBounds = null,
    debugMode = false,
  } = options;

  const warnings = [];

  if (debugMode && candidateBounds && validateRadarBounds(candidateBounds).valid) {
    return {
      bounds: candidateBounds,
      boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.CANDIDATE_PREVIEW,
      boundsConfirmed: false,
      confidence: "low",
      boundsValid: true,
      calibrationVersion: serverGeoref?.calibrationVersion || null,
      warnings: ["Using admin calibration preview bounds (not saved)."],
    };
  }

  const geotiffBounds = resolveGeotiffBounds(georefFrame, serverGeoref);
  if (geotiffBounds) {
    return {
      bounds: geotiffBounds,
      boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.GEOTIFF,
      boundsConfirmed: true,
      confidence: serverGeoref?.confidence || georefFrame?.geotiffMetadata?.confidence || "high",
      boundsValid: true,
      calibrationVersion: serverGeoref?.calibrationVersion || null,
      warnings: serverGeoref?.warnings || georefFrame?.geotiffMetadata?.warnings || [],
    };
  }

  const envBounds = parseBoundsEnv(process.env.REACT_APP_SIATA_SANTA_ELENA_RADAR_BOUNDS);
  if (envBounds && validateRadarBounds(envBounds).valid) {
    return {
      bounds: envBounds,
      boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.ENV_OVERRIDE,
      boundsConfirmed: false,
      confidence: "medium",
      boundsValid: true,
      calibrationVersion: serverGeoref?.calibrationVersion || null,
      warnings,
    };
  }

  if (
    serverGeoref?.activeGeorefSource === SIATA_SANTA_ELENA_GEOREF_SOURCES.ADMIN_CALIBRATED &&
    serverGeoref?.boundsValid &&
    serverGeoref?.bounds &&
    validateRadarBounds(serverGeoref.bounds).valid
  ) {
    return {
      bounds: serverGeoref.bounds,
      boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.ADMIN_CALIBRATED,
      boundsConfirmed: serverGeoref.boundsConfirmed === true,
      confidence: serverGeoref.confidence || "medium",
      boundsValid: true,
      calibrationVersion: serverGeoref.calibrationVersion || null,
      warnings: serverGeoref.warnings || [],
    };
  }

  if (
    serverGeoref?.boundsValid &&
    serverGeoref?.bounds &&
    validateRadarBounds(serverGeoref.bounds).valid
  ) {
    return {
      bounds: serverGeoref.bounds,
      boundsSource:
        serverGeoref.activeGeorefSource || SIATA_SANTA_ELENA_GEOREF_SOURCES.APPROVED_MANUAL,
      boundsConfirmed: serverGeoref.boundsConfirmed === true,
      confidence: serverGeoref.confidence || "medium",
      boundsValid: true,
      calibrationVersion: serverGeoref.calibrationVersion || null,
      warnings: serverGeoref.warnings || [],
    };
  }

  const approved = { ...SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS };
  if (validateRadarBounds(approved).valid) {
    return {
      bounds: approved,
      boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.APPROVED_MANUAL,
      boundsConfirmed: false,
      confidence: "medium",
      boundsValid: true,
      calibrationVersion: serverGeoref?.calibrationVersion || null,
      warnings: [...warnings, "Using approved manual bounds pending GeoTIFF calibration."],
    };
  }

  if (!isProductionEnv()) {
    const placeholder = { ...SIATA_SANTA_ELENA_RADAR_DEV_PLACEHOLDER_BOUNDS };
    return {
      bounds: placeholder,
      boundsSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.DEVELOPMENT_PLACEHOLDER,
      boundsConfirmed: false,
      confidence: "low",
      boundsValid: validateRadarBounds(placeholder).valid,
      calibrationVersion: serverGeoref?.calibrationVersion || null,
      warnings: [...warnings, "Using development placeholder bounds."],
    };
  }

  return {
    bounds: null,
    boundsSource: null,
    boundsConfirmed: false,
    confidence: "low",
    boundsValid: false,
    calibrationVersion: serverGeoref?.calibrationVersion || null,
    warnings: [...warnings, "No valid georeference source in production."],
  };
}

export function getSiataSantaElenaRadarCoordinates(frameOrConfig = null) {
  const options =
    frameOrConfig?.georefFrame || frameOrConfig?.serverGeoref
      ? frameOrConfig
      : { georefFrame: frameOrConfig };

  const resolved = resolveSiataSantaElenaRadarBounds(options);
  if (!resolved.boundsValid || !resolved.bounds) {
    return null;
  }

  if (frameOrConfig?.mapboxCoordinates && Array.isArray(frameOrConfig.mapboxCoordinates)) {
    return frameOrConfig.mapboxCoordinates;
  }

  return boundsToMapboxCoordinates(resolved.bounds);
}

export function getSiataSantaElenaRadarBoundsGeoJson(
  bounds = resolveSiataSantaElenaRadarBounds().bounds,
  properties = { debug: true, label: "SIATA bounds (debug)" }
) {
  if (!bounds) {
    return { type: "FeatureCollection", features: [] };
  }
  return boundsToGeoJson(bounds, properties);
}

export function clampSiataSantaElenaRadarOpacity(opacity) {
  const value = Number(opacity);
  if (!Number.isFinite(value)) {
    return SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG.defaultOpacity;
  }
  return Math.min(1, Math.max(0, value));
}

export function isSiataSantaElenaRadarOverlayDebugEnabled(options = {}) {
  if (options.forceEnabled) {
    return true;
  }

  if (process.env.REACT_APP_SIATA_SANTA_ELENA_RADAR_OVERLAY_DEBUG === "true") {
    return true;
  }

  if (typeof window !== "undefined" && window.__MIDPOINT_SIATA_RADAR_OVERLAY_DEBUG__ === true) {
    return true;
  }

  return false;
}

export function isSiataSantaElenaRadarDebugBoundsEnabled(options = {}) {
  if (options.forceEnabled) {
    return true;
  }

  if (process.env.NODE_ENV === "production") {
    return false;
  }

  if (process.env.REACT_APP_SIATA_SANTA_ELENA_RADAR_DEBUG_BOUNDS === "true") {
    return true;
  }

  if (typeof window !== "undefined" && window.__MIDPOINT_SIATA_DEBUG_BOUNDS__ === true) {
    return true;
  }

  return false;
}

export function isSiataSantaElenaRadarCalibrationDebugEnabled(options = {}) {
  if (options.forceEnabled) {
    return true;
  }

  if (typeof window !== "undefined" && window.__MIDPOINT_SIATA_CALIBRATION_DEBUG__ === true) {
    return true;
  }

  return isSiataSantaElenaRadarDebugBoundsEnabled(options);
}
